import type {
  Carer,
  Home,
  LogCategoryItem,
  LogResponse,
  Resident
} from '@lmc/ts-common'
import { AvatarLabel, Heading, Text } from '@lmc/ui-kit'
import useGoalsAccess from 'components/AccessControl/features/useGoalsAccess'
import { Gallery, GalleryItem } from 'components/Gallery'
import dayjs from 'dayjs'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import GoalsList from 'screens/Residents/screens/ResidentsGoalsScreen/components/GoalsList/GoalsList'
import useGoalsIDs from 'screens/Residents/screens/ResidentsGoalsScreen/components/useGoals/useGoalsIDs'
import styled from 'styled-components'
import LastUpdateBox from '../../../../components/carePlans/LastUpdateBox'
import GeoLocation from '../../../../components/GeoLocation/GeoLocation'
import { history } from '../../../../store'
import style from '../../screens/LogScreen.module.less'
import { isSeizureItemLog } from '../../utils/logUtils'
import LogMeasurements from './LogMeasurements'
import LogRevisions from './LogRevisions'
import PainChekAssessment from './PainChekAssessment'

// @ts-ignore
interface LogWithRevisions extends LogResponse {
  logRevisions?: LogResponse[]
  externalId?: string
  picture1?: string
  picture2?: string
  picture3?: string
  editedOn?: string
  reasonCode?: string
}

interface Props {
  log: LogWithRevisions
  resident: Resident
  item: LogCategoryItem
  loggedBy: Carer
  witnessedBy: Carer
  editedBy: Carer
  features: Home
  painChekAssessmentResult: any // TODO: import type from TS-Common
  testId?: string
}

export const LogDetails = ({
  log,
  resident,
  item,
  loggedBy,
  witnessedBy,
  editedBy,
  features,
  painChekAssessmentResult,
  testId
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const renderResidentAvatar = () => {
    if (!resident) return null
    const pic = resident.picture as unknown as string
    return (
      <div
        style={styles.avatarContainer}
        data-testid={testId ? `${testId}-resident-profile` : 'resident-profile'}
      >
        <AvatarLabel
          onSelect={() => history.push(`/residents/${resident?.id}/profile`)}
          label={`${resident.name.first} ${resident.name.last}`}
          picture={pic}
          testId={testId ? `${testId}-resident-avatar` : 'resident-avatar'}
        />
      </div>
    )
  }

  const renderImages = () => {
    const images = [log.picture1, log.picture2, log.picture3]
    const { videoFile } = log

    const validImages = images.filter((url) => url)

    const shouldRender: boolean = Boolean(videoFile || validImages.length)

    if (!shouldRender) {
      return null
    }

    return (
      <div
        style={styles.mediaContainer}
        data-testid={
          testId ? `${testId}-attached-media-section` : 'attached-media-section'
        }
      >
        <Text as="h4">{t('components.logEditForm.attachedMedia')}</Text>
        <MediaWrapper>
          {Boolean(validImages.length) && (
            <div className={style.imagesContainer}>
              <Gallery>
                {validImages.map((url, index) => (
                  <GalleryItem
                    key={`image-${index + 1}`}
                    src={url || ''}
                    alt="Log photo"
                    className={style.image}
                    width={749}
                    height={1000}
                  />
                ))}
              </Gallery>
            </div>
          )}
          {Boolean(videoFile) && (
            <Video controls>
              <source src={videoFile} type="video/mp4" />
              <track
                src="captions_en.vtt"
                kind="captions"
                label="english_captions"
              />
            </Video>
          )}
        </MediaWrapper>
      </div>
    )
  }

  const renderMeasurements = (renderLink?: boolean) => {
    if (item && (item.amountOneKey || item.amountTwoKey)) {
      return (
        <LogMeasurements
          log={log}
          item={item}
          onClick={(url: string) => history.push(url)}
          renderLink={renderLink}
        />
      )
    }

    return null
  }

  const hasGoals = useGoalsAccess()
  const { filteredGoals: goals } = useGoalsIDs({
    goalsIDs: log.goals as string[],
    residentID: resident?.id
  })

  const showLinkOnly = isSeizureItemLog(item?.id)

  return (
    <div data-testid={testId ? `${testId}-details` : 'log-details'}>
      {renderResidentAvatar()}
      <div
        style={styles.descriptionContainer}
        data-testid={
          testId ? `${testId}-description-section` : 'log-description-section'
        }
      >
        <Text as="p2" style={styles.title}>
          {t('components.logEditForm.description')}
        </Text>
        <br />
        <Text
          as="p2"
          style={styles.descriptionText}
          data-testid={
            testId ? `${testId}-description-text` : 'log-description-text'
          }
        >
          {log.description}
        </Text>
        {witnessedBy && (
          <div
            style={styles.witnessContainer}
            data-testid={
              testId ? `${testId}-witnessed-by-section` : 'witnessed-by-section'
            }
          >
            <Text as="p2" style={styles.title}>
              {t('components.logEditForm.witnessedBy', { name: '' })}
            </Text>
            <br />
            <Text
              as="p2"
              style={styles.descriptionText}
              data-testid={
                testId ? `${testId}-witnessed-by-name` : 'witnessed-by-name'
              }
            >
              {witnessedBy.name.first} {witnessedBy.name.last}
            </Text>
          </div>
        )}
        {renderMeasurements(showLinkOnly)}
        {log.reasonCode ? (
          <div
            style={styles.outcome}
            data-testid={
              testId ? `${testId}-outcome-section` : 'outcome-section'
            }
          >
            <Text as="p2" style={styles.title}>
              {'Outcome: '}
            </Text>
            <Text
              as="p2"
              style={styles.descriptionText}
              data-testid={testId ? `${testId}-outcome-value` : 'outcome-value'}
            >
              {log.reasonCode}
            </Text>
          </div>
        ) : null}
      </div>
      <div
        style={styles.lastUpdateContainer}
        data-testid={
          testId ? `${testId}-time-logged-section` : 'time-logged-section'
        }
      >
        <LastUpdateBox
          title={t('components.logEditForm.timeLogged')}
          carer={loggedBy}
          // @ts-ignore
          date={log.timeLogged}
          noDateText=""
        >
          {editedBy || witnessedBy ? (
            <div style={styles.metaText}>
              <Text as="p2">
                {witnessedBy &&
                  t('components.logEditForm.witnessedBy', {
                    name: `${witnessedBy.name.first} ${witnessedBy.name.last}`
                  })}
                {editedBy &&
                  `• ${t('components.logEditForm.editedBy', {
                    name: `${editedBy.name.first} ${editedBy.name.last}`,
                    day: dayjs(log.editedOn).format('DD/MM/YYYY HH:mm')
                  })}`}
              </Text>
            </div>
          ) : null}
        </LastUpdateBox>
      </div>
      {hasGoals && goals && (
        <div data-testid={testId ? `${testId}-goals-section` : 'goals-section'}>
          <GoalsListStyled
            residentID={resident?.id}
            items={goals}
            title={t('components.logEditForm.linkedGoals')}
          />
        </div>
      )}
      {renderImages()}
      {painChekAssessmentResult && (
        <PainChekAssessment assessmentResult={painChekAssessmentResult} />
      )}
      {features.features?.geoLocation?.enabled && (
        <div
          data-testid={
            testId ? `${testId}-location-section` : 'location-section'
          }
        >
          <Heading level={4}>
            {t('components.logEditForm.locationAvailable')}
          </Heading>
          <GeolocationContainer>
            <GeoLocation
              longitude={log.longitude}
              latitude={log.latitude}
              accuracy={log.accuracy}
            />
          </GeolocationContainer>
        </div>
      )}

      {log.logRevisions && log.logRevisions.length ? (
        <div
          className={style.logRevisions}
          data-testid={
            testId ? `${testId}-log-revisions-section` : 'log-revisions-section'
          }
        >
          <Text as="p2">{t('components.logEditForm.logRevisions')}</Text>
          <LogRevisions log={log} />
        </div>
      ) : null}
    </div>
  )
}

const GoalsListStyled = styled(GoalsList)`
  margin-top: 40px;
  margin-bottom: 80px;
`

const styles = {
  descriptionContainer: {
    background: 'white',
    borderRadius: 16,
    padding: '16px 24px 18px 24px'
  } as React.CSSProperties,
  descriptionText: {
    lineHeight: '24px',
    whiteSpace: 'pre-wrap'
  } as React.CSSProperties,
  title: { fontWeight: 'bold' },
  outcome: { paddingTop: 12 },
  lastUpdateContainer: { paddingTop: 12, paddingBottom: 32 },
  mediaContainer: { paddingBottom: 48 },
  avatarContainer: { paddingTop: 12, paddingBottom: 24, fontWeight: 'bold' },
  metaText: { paddingTop: 8, paddingBottom: 8 },
  witnessContainer: { paddingTop: 12 }
}

const Video = styled.video`
  width: 320px;
  height: 180px;
`

const GeolocationContainer = styled.div`
  margin-top: 12px;
`

const MediaWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
`

export default LogDetails
